import { useEffect, useState, useCallback, useRef } from "react";
import { useAppSelector } from "src/common/state/hooks";
import axios from "axios";
import apiUrls from "../../../api";
import { getInstallJob } from "../state/deviceInstallerSlice";
import { compareVersions } from "../utils/versionName";
import { POLL_INTERVAL_MS, SPATIAL_STUDIO_AGENT_ID } from "../constants";
import type { AgentInfo } from "./useAgentConnection";
import type { SpatialStudioStatus, SpatialStudioState } from "./useSpatialStudioStatus";
import type { IEditorVrBuild } from "../../../model/model";

/** Platform IDs in the editor VR builds API (from `platforms` table) */
const WINDOWS_PLATFORM_ID = 2;
const MACOS_PLATFORM_ID = 3;

export function useAgentSpatialStudioStatus(agentInfo: AgentInfo | null): SpatialStudioState {
  const installJob = useAppSelector(getInstallJob);

  const [status, setStatus] = useState<SpatialStudioStatus>("loading");
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const [latestBuild, setLatestBuild] = useState<IEditorVrBuild | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [s3Url, setS3Url] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [buildsLoaded, setBuildsLoaded] = useState(false);

  const refresh = useCallback(() => setRefreshCounter((c) => c + 1), []);

  // Fetch latest builds from API (once)
  const buildsRef = useRef<IEditorVrBuild[]>([]);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(apiUrls.getMostRecentEditorVrBuildPerPlatform)
      .then((res) => {
        if (!cancelled) {
          buildsRef.current = res.data || [];
          setBuildsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setBuildsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Determine status from agent info + latest builds
  useEffect(() => {
    if (!agentInfo || !buildsLoaded) {
      setStatus("loading");
      return;
    }

    // Find the build matching the agent's OS
    const targetPlatformId =
      agentInfo.os.platform === "darwin" ? MACOS_PLATFORM_ID : WINDOWS_PLATFORM_ID;
    const build = buildsRef.current.find((b) => b.platform_id?.id === targetPlatformId);

    if (!build) {
      setStatus("not_available");
      setLatestBuild(null);
      setLatestVersion(null);
      setS3Url(null);
      return;
    }

    setLatestBuild(build);
    setLatestVersion(build.version ?? null);
    setS3Url(build.source_zip?.s3_url ?? null);

    // Check if Spatial Studio is installed via agent registry
    const installed = agentInfo.installedApps.find((a) => a.id === SPATIAL_STUDIO_AGENT_ID);

    if (!installed) {
      setInstalledVersion(null);
      setStatus("not_installed");
    } else {
      setInstalledVersion(installed.version);
      if (build.version && compareVersions(installed.version, build.version) < 0) {
        setStatus("update_available");
      } else {
        setStatus("up_to_date");
      }
    }
  }, [agentInfo, buildsLoaded, refreshCounter]);

  // Poll periodically (refresh agent info triggers parent to re-fetch)
  const isInstalling =
    installJob != null && installJob.status !== "success" && installJob.status !== "error";

  useEffect(() => {
    if (!agentInfo || isInstalling) return undefined;
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [agentInfo, isInstalling, refresh]);

  return { status, installedVersion, latestVersion, latestBuild, s3Url, refresh };
}
